import React from "react";
import { useSelector } from "react-redux";
import { ThemeProvider, useTheme, JssProvider } from "react-jss";
import { PageContainer } from "../containers/page/PageContainer";
import { getContainerDiv } from "../utils";
import { MobilePreview } from "./shared/MobilePreview";
import useStyles from "./SurveyEngine.styles";
import useTranslatable from "../hooks/useTranslatable";
import DefaultTheme from "../theme/DefaultTheme";
import { StoreState } from "../models/states/StoreState";
import { ErrorLogContainer } from "../containers/ErrorLogContainer";
import { Voxco } from "../public";
import { FontAwesomeIcon } from "./shared/FontAwesome";
import { SurveyMode } from "../models/enums/SurveyMode";
import useReservedBounds, {
    ReservedBoundsContextProvider,
    ReservedBounds,
} from "../hooks/useReservedBounds";
import { DebugPanel } from "./debug/DebugPanel";

interface Props {
    bounds: ReservedBounds;
}

function SimulatedBanner({ bounds }) {
    const theme = useTheme();
    const classes = useStyles({
        bounds,
        theme,
    });
    useReservedBounds({ top: 38 }, []);
    const questionnaire = useSelector((state: StoreState) => state.interview.questionnaire);
    const previewModeMessage = useTranslatable(questionnaire, "previewModeMessage");

    return (
        <div className={classes.simulatedBanner} role="region" aria-label="Preview">
            <FontAwesomeIcon icon={["far", "eye"]} fixedWidth />
            <span className={classes.simulatedBannerMessage} role="alert">
                {previewModeMessage}
            </span>
        </div>
    );
}

function SurveyEngine({ bounds }: Props) {
    const simulated = useSelector(
        (state: StoreState) =>
            process.env.NODE_ENV !== "development" && // Do not show simulated warning if we are in dev.
            state.interview.respondent &&
            state.interview.respondent.simulated,
    );

    const debugPanelVisible =
        useSelector(
            (state: StoreState) =>
                state.application.mode === SurveyMode.Preview ||
                state.application.mode === SurveyMode.Test,
        ) && process.env.NODE_ENV !== "test";
    const mobilePreview = useSelector((state: StoreState) => state.mobilePreview);
    const simulatedBannerVisible =
        useSelector((state: StoreState) => state.application.mode !== SurveyMode.Normal) &&
        simulated;

    const theme = useTheme();
    const classes = useStyles({
        bounds,
        theme,
        mobilePreview,
        simulatedBannerVisible,
    });

    const { CustomCSS, CustomJavascript } = theme as any;

    React.useEffect(() => {
        // Adjust height if debug panel is shown.
        const surveyEngine = getContainerDiv();
        const debugPanel = document.getElementsByClassName("debug-pane")[0] as HTMLElement;
        if (debugPanel != null) {
            const { height } = debugPanel.getBoundingClientRect();
            surveyEngine.style.bottom = `${height}px`;
        }
    }, []);

    React.useEffect(() => {
        // eslint-disable-next-line
        (function customJs(console) {
            try {
                // eslint-disable-next-line
                eval(`${CustomJavascript}\n//# sourceURL=custom.js`);
            } catch (ex) {
                Voxco.error(ex);
            }
        }.call(Voxco, { log: Voxco.log, error: Voxco.error }));
    }, [CustomJavascript]);

    return (
        <>
            {simulatedBannerVisible && <SimulatedBanner bounds={bounds} />}
            <div className={classes.surveyEngine}>
                {debugPanelVisible && mobilePreview ? (
                    <MobilePreview
                        renderDesktop={() => <PageContainer isDesktopPreviewView={true} />}
                        renderMobile={() => <PageContainer isDesktopPreviewView={false} />}
                    />
                ) : (
                    <PageContainer isDesktopPreviewView={null} />
                )}
                <style>{CustomCSS || ""}</style>
            </div>
            {debugPanelVisible && <DebugPanel />}
        </>
    );
}

export function ThemedSurveyEngine() {
    const hasQuestionnaire = useSelector(({ interview }: StoreState) =>
        Boolean(interview.questionnaire),
    );

    const hasMessage = useSelector(({ interview }: StoreState) =>
        Boolean(interview.navigation && interview.navigation.message),
    );

    const settings = useSelector(
        ({ interview }: StoreState) => interview.theme && interview.theme.settings,
    );

    const themeSettings = React.useMemo(
        () => ({
            // Setup default settings for theme.
            ...DefaultTheme,
            // Override with theme settings.
            ...settings,
        }),
        [settings],
    );

    return (
        <div role="region" aria-live="polite">
            {hasQuestionnaire || hasMessage ? (
                <JssProvider>
                    <ThemeProvider theme={themeSettings}>
                        <ReservedBoundsContextProvider>
                            {(bounds) => <SurveyEngine bounds={bounds} />}
                        </ReservedBoundsContextProvider>
                    </ThemeProvider>
                </JssProvider>
            ) : (
                <ErrorLogContainer />
            )}
        </div>
    );
}
